import { z } from 'zod'

export const emailSchema = z.string().min(1, 'Email is required').email('Please enter a valid email address')
export const passwordSchema = z.string().min(1, 'Password is required')
export const confirmPasswordSchema = (minLength = 6) =>
  z.string().min(minLength, `Password must be at least ${minLength} characters`)

export const loginSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
})

export const clientSchema = z.object({
  name: z.string().min(1, 'Name is required').trim(),
  email: emailSchema,
  logo_url: z
    .string()
    .optional()
    .refine((v) => !v || v === '' || /^https?:\/\/.+/.test(v), 'Enter a valid URL'),
  cover_image_url: z
    .string()
    .optional()
    .refine((v) => !v || v === '' || /^https?:\/\/.+/.test(v), 'Enter a valid URL'),
})

export const campaignSchema = z.object({
  name: z.string().min(1, 'Campaign name is required').trim(),
  description: z.string().optional(),
  status: z.enum(['draft', 'active', 'completed', 'archived']).optional(),
  client_id: z.string().optional(),
  budget: z
    .union([z.string(), z.number()])
    .optional()
    .transform((v) => (v === '' || v == null ? null : parseFloat(String(v))))
    .pipe(z.number().min(0, 'Budget must be 0 or more').nullable()),
  budget_notes: z.string().optional(),
})

/**
 * @param {{ requireClientId?: boolean }} [options]
 */
export function getCampaignFormSchema({ requireClientId = false } = {}) {
  const withSlotTemplates = campaignSchema.merge(campaignSlotTemplatesSchema)
  if (!requireClientId) return withSlotTemplates
  return withSlotTemplates.refine((data) => Boolean(data.client_id), {
    message: 'Please select a client',
    path: ['client_id'],
  })
}

export const contentUploadSchema = z.object({
  // Empty string must be allowed: inline upload (fixed campaign) never sets client/campaign pickers.
  selectedClientId: z.string().optional(),
  selectedCampaignId: z.string().optional(),
  title: z.string().trim().min(1, 'Title is required'),
  caption: z.string().trim().min(1, 'Caption is required'),
  contentType: z.string().optional(),
})

export const joinSchema = z
  .object({
    password: confirmPasswordSchema(6),
    confirmPassword: z.string(),
    firstName: z.string().min(1, 'First name is required').trim(),
    lastName: z.string().min(1, 'Last name is required').trim(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

/** First login password setup (stricter than invite join). */
export const completeOnboardingSchema = z
  .object({
    firstName: z.string().min(1, 'First name is required').trim(),
    lastName: z.string().min(1, 'Last name is required').trim(),
    password: confirmPasswordSchema(8),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

export const inviteEmailSchema = z.object({
  email: emailSchema,
})

export const slotSchema = z.object({
  title: z.string().trim().min(1, 'Title is required'),
  type: z.enum(['video', 'image', 'stories'], { required_error: 'Content type is required' }),
  platform: z.enum(['instagram', 'tiktok', 'youtube', 'facebook', 'linkedin', 'other'], {
    required_error: 'Platform is required',
  }),
  due_date: z.string().optional(),
  post_live_date: z.string().optional(),
  notes: z.string().optional(),
})

export const createSlotsSchema = z.object({
  slots: z.array(slotSchema).min(1, 'Add at least one slot'),
})

export const campaignSlotTemplateSchema = slotSchema

export const campaignSlotTemplatesSchema = z.object({
  slot_templates: z.array(campaignSlotTemplateSchema).optional().default([]),
})

/** @returns {Record<string, string>|null} First error per field for drawer editors. */
export function getSlotFieldErrors(draft) {
  const parsed = slotSchema.safeParse({
    title: draft?.title ?? '',
    type: draft?.type || 'video',
    platform: draft?.platform || 'instagram',
    due_date: draft?.due_date ?? '',
    post_live_date: draft?.post_live_date ?? '',
    notes: draft?.notes ?? '',
  })
  if (parsed.success) return null
  /** @type {Record<string, string>} */
  const fieldErrors = {}
  for (const issue of parsed.error.issues) {
    const key = issue.path[0]
    if (typeof key === 'string' && !fieldErrors[key]) fieldErrors[key] = issue.message
  }
  return fieldErrors
}

export const profileSchema = z
  .object({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    bio: z.string().optional(),
    instagramHandle: z.string().optional(),
    tiktokHandle: z.string().optional(),
    youtubeHandle: z.string().optional(),
    websiteUrl: z.string().optional(),
    location: z.string().optional(),
    phone: z.string().optional(),
    followerCount: z.union([z.string(), z.number()]).optional().transform((v) => (v === '' || v == null ? null : parseInt(String(v), 10))).pipe(z.number().int().min(0).nullable()),
    engagementRate: z.union([z.string(), z.number()]).optional().transform((v) => (v === '' || v == null ? null : parseFloat(String(v)))).pipe(z.number().min(0).max(100).nullable()),
    rate: z.union([z.string(), z.number()]).optional().transform((v) => (v === '' || v == null ? null : parseFloat(String(v)))).pipe(z.number().min(0).nullable()),
    nicheTags: z.union([z.string().transform((s) => (s ? s.split(/[,\s]+/).filter(Boolean) : [])), z.array(z.string())]).optional().default([]),
  })
  .refine((data) => (data.firstName || '').trim() || (data.lastName || '').trim(), {
    message: 'First name or last name is required',
    path: ['firstName'],
  })
  .refine(
    (data) => {
      const url = (data.websiteUrl || '').trim()
      if (!url) return true
      try {
        new URL(url.startsWith('http') ? url : `https://${url}`)
        return true
      } catch {
        return false
      }
    },
    { message: 'Please enter a valid website URL', path: ['websiteUrl'] }
  )

export const influencerOnboardingSchema = z
  .object({
    firstName: z.string().min(1, 'First name is required').trim(),
    lastName: z.string().min(1, 'Last name is required').trim(),
    bio: z.string().optional(),
    instagramHandle: z.string().optional(),
    tiktokHandle: z.string().optional(),
    youtubeHandle: z.string().optional(),
    websiteUrl: z.string().optional(),
    location: z.string().optional(),
    phone: z.string().optional(),
    followerCount: z
      .union([z.string(), z.number()])
      .optional()
      .transform((v) => (v === '' || v == null ? null : parseInt(String(v), 10)))
      .pipe(z.number().int().min(0).nullable()),
    engagementRate: z
      .union([z.string(), z.number()])
      .optional()
      .transform((v) => (v === '' || v == null ? null : parseFloat(String(v))))
      .pipe(z.number().min(0).max(100).nullable()),
    rate: z
      .union([z.string(), z.number()])
      .optional()
      .transform((v) => (v === '' || v == null ? null : parseFloat(String(v))))
      .pipe(z.number().min(0).nullable()),
    nicheTags: z
      .union([
        z.string().transform((s) => (s ? s.split(/[,\s]+/).filter(Boolean) : [])),
        z.array(z.string()),
      ])
      .optional()
      .default([]),
  })
  .refine(
    (data) => {
      const url = (data.websiteUrl || '').trim()
      if (!url) return true
      try {
        new URL(url.startsWith('http') ? url : `https://${url}`)
        return true
      } catch {
        return false
      }
    },
    { message: 'Please enter a valid website URL', path: ['websiteUrl'] }
  )

export const contentEditSchema = z.object({
  content_type: z.string().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
})

export const settingsFeedbackSchema = z.object({
  category: z.enum(['general', 'bug', 'ux', 'performance']),
  summary: z.string().trim().min(1, 'Please describe what happened'),
})

/**
 * @param {{ emailOptional?: boolean }} [options]
 */
export function buildInfluencerProfileSchema({ emailOptional = false } = {}) {
  return z
    .object({
      firstName: z.string().min(1, 'First name is required').trim(),
      lastName: z.string().min(1, 'Last name is required').trim(),
      email: emailOptional
        ? z
            .string()
            .optional()
            .refine((v) => !v || v.trim() === '' || z.string().email().safeParse(v.trim()).success, {
              message: 'Please enter a valid email address',
            })
        : emailSchema,
      bio: z.string().optional(),
      instagramHandle: z.string().optional(),
      tiktokHandle: z.string().optional(),
      youtubeHandle: z.string().optional(),
      websiteUrl: z.string().optional(),
      location: z.string().optional(),
      phone: z.string().optional(),
      followerCount: z
        .union([z.string(), z.number()])
        .optional()
        .transform((v) => (v === '' || v == null ? null : parseInt(String(v), 10)))
        .pipe(z.number().int().min(0).nullable()),
      engagementRate: z
        .union([z.string(), z.number()])
        .optional()
        .transform((v) => (v === '' || v == null ? null : parseFloat(String(v))))
        .pipe(z.number().min(0).max(100).nullable()),
      rate: z
        .union([z.string(), z.number()])
        .optional()
        .transform((v) => (v === '' || v == null ? null : parseFloat(String(v))))
        .pipe(z.number().min(0).nullable()),
      nicheTags: z.string().optional(),
    })
    .refine(
      (data) => {
        if (emailOptional) return true
        return Boolean((data.email || '').trim())
      },
      { message: 'Email is required', path: ['email'] },
    )
    .refine(
      (data) => {
        const url = (data.websiteUrl || '').trim()
        if (!url) return true
        try {
          new URL(url.startsWith('http') ? url : `https://${url}`)
          return true
        } catch {
          return false
        }
      },
      { message: 'Please enter a valid website URL', path: ['websiteUrl'] },
    )
}
